import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { container } from '@/di';
import { TYPES } from '@/di/tokens';
import { GetTenantAction, UpdateTenantAction } from '@/application/actions';
import { useToast } from '@/presentation/contexts/ToastContext';
import { usePlans } from '@/presentation/hooks/usePlans';
import { Button, Input, ModalShell } from '@/presentation/components/common';
import {
  TenantCommercialPacksSection,
  type CommercialChangeLine,
  type TenantCommercialPacksHandle,
} from '@/presentation/components/tenants/TenantCommercialPacksSection';
import type { TenantGridDTO, TenantFormData } from '@/domain/repositories/ITenantRepository';
import { TIMEZONES } from '@/shared/constants/timezones';
import { isRetiredIntermedioPlan } from '@/domain/types/planCatalog';
import { httpClient } from '@/infrastructure/http/client/httpClient';
import {
  listPendingPlanChanges,
  cancelPlanChange,
  type PlanChangeDTO,
} from '@/infrastructure/api/planChangeApi';

interface EditTenantModalProps {
  isOpen: boolean;
  tenant: TenantGridDTO;
  onClose: () => void;
  onSuccess: () => void;
}

type EditTenantTab = 'data' | 'commercial';

function sameDate(a?: Date, b?: Date): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.getTime() === b.getTime();
}

export const EditTenantModal: React.FC<EditTenantModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();
  const { plans } = usePlans();
  const commercialRef = useRef<TenantCommercialPacksHandle>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [activeTab, setActiveTab] = useState<EditTenantTab>('data');
  const [confirmLines, setConfirmLines] = useState<CommercialChangeLine[] | null>(null);
  const [commercialDirty, setCommercialDirty] = useState(false);
  const [formData, setFormData] = useState<TenantFormData>({
    nombre: '',
    connectionString: '',
    emailAdministrador: '',
    telefonoContacto: '',
    baseDeDatos: '',
    timeZoneId: 'UTC',
    fechaVencimiento: undefined,
    planAdminId: undefined,
  });
  const [savedForm, setSavedForm] = useState<TenantFormData | null>(null);
  const [pendingPlanChange, setPendingPlanChange] = useState<PlanChangeDTO | null>(null);
  const [forcePlan, setForcePlan] = useState(false);
  const [forcePlanReason, setForcePlanReason] = useState('');
  const [cancelingPlanChange, setCancelingPlanChange] = useState(false);

  const availableTimezones = TIMEZONES;
  const formId = 'edit-tenant-form';

  useEffect(() => {
    if (isOpen && tenant) {
      setActiveTab('data');
      setCommercialDirty(false);
      setConfirmLines(null);
      setForcePlan(false);
      setForcePlanReason('');
      void loadTenantData();
    }
  }, [isOpen, tenant]);

  const loadTenantData = async () => {
    setIsLoadingData(true);
    try {
      const action = container.get<GetTenantAction>(TYPES.GetTenantAction);
      const [tenantData, raw, pending] = await Promise.all([
        action.execute(tenant.id),
        httpClient.get<{ telefonoContacto?: string | null }>(
          `/tenants/${encodeURIComponent(String(tenant.id))}`
        ),
        listPendingPlanChanges(String(tenant.id)).catch(() => []),
      ]);
      setPendingPlanChange(pending.find((p) => p.tipo === 'PLAN_BASE') ?? null);
      const timeZone = availableTimezones.includes(tenantData.timeZoneId)
        ? tenantData.timeZoneId
        : 'UTC';

      setFormData({
        nombre: tenantData.nombre,
        connectionString: tenantData.connectionString,
        emailAdministrador: tenantData.emailAdministrador.getValue(),
        telefonoContacto: raw.data?.telefonoContacto || '',
        baseDeDatos: tenantData.baseDeDatos,
        timeZoneId: timeZone,
        fechaVencimiento: tenantData.fechaVencimiento,
        planAdminId: tenantData.planAdminId,
      });
      setSavedForm({
        nombre: tenantData.nombre,
        connectionString: tenantData.connectionString,
        emailAdministrador: tenantData.emailAdministrador.getValue(),
        telefonoContacto: raw.data?.telefonoContacto || '',
        baseDeDatos: tenantData.baseDeDatos,
        timeZoneId: timeZone,
        fechaVencimiento: tenantData.fechaVencimiento,
        planAdminId: tenantData.planAdminId,
      });
      setConfirmLines(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al cargar tenant');
      showError(error.message);
    } finally {
      setIsLoadingData(false);
    }
  };

  const planName = (id?: number) => {
    if (!id) return t('pages.tenants.selectPlan');
    return plans.find((p) => p.id === id)?.nombre ?? String(id);
  };

  const summarizeFormChanges = (): CommercialChangeLine[] => {
    if (!savedForm) return [];
    const lines: CommercialChangeLine[] = [];
    if (formData.nombre !== savedForm.nombre) {
      lines.push({ label: t('pages.tenants.name'), value: `${savedForm.nombre} → ${formData.nombre}` });
    }
    if (formData.emailAdministrador !== savedForm.emailAdministrador) {
      lines.push({
        label: t('pages.tenants.email'),
        value: `${savedForm.emailAdministrador} → ${formData.emailAdministrador}`,
      });
    }
    if ((formData.telefonoContacto || '') !== (savedForm.telefonoContacto || '')) {
      lines.push({
        label: t('pages.tenants.phone'),
        value: `${savedForm.telefonoContacto || '—'} → ${formData.telefonoContacto || '—'}`,
      });
    }
    if (!sameDate(formData.fechaVencimiento, savedForm.fechaVencimiento)) {
      const fmt = (d?: Date) =>
        d ? new Date(d).toLocaleDateString('es-AR') : '—';
      lines.push({
        label: t('pages.tenants.expirationDate'),
        value: `${fmt(savedForm.fechaVencimiento)} → ${fmt(formData.fechaVencimiento)}`,
      });
    }
    if (formData.baseDeDatos !== savedForm.baseDeDatos) {
      lines.push({
        label: t('pages.tenants.database'),
        value: `${savedForm.baseDeDatos} → ${formData.baseDeDatos}`,
      });
    }
    if (formData.timeZoneId !== savedForm.timeZoneId) {
      lines.push({
        label: t('pages.tenants.timeZone'),
        value: `${savedForm.timeZoneId} → ${formData.timeZoneId}`,
      });
    }
    if (formData.planAdminId !== savedForm.planAdminId) {
      const timing = !savedForm.planAdminId
        ? ' (inmediato · alta)'
        : forcePlan
          ? ' (inmediato · forzado)'
          : ' (rige desde el próximo período)';
      lines.push({
        label: t('pages.tenants.saasPlanTitle'),
        value: `${planName(savedForm.planAdminId)} → ${planName(formData.planAdminId)}${timing}`,
      });
    }
    return lines;
  };

  const persistAll = async () => {
    const action = container.get<UpdateTenantAction>(TYPES.UpdateTenantAction);
    await action.execute(tenant.id, {
      ...formData,
      forzarInmediato: saasDirty ? forcePlan : undefined,
      motivo: saasDirty && forcePlan ? forcePlanReason.trim() : undefined,
    });
    if (commercialRef.current?.hasChanges()) {
      await commercialRef.current.commit();
    }
    success(t('pages.tenants.updatedSuccess'));
    setConfirmLines(null);
    onSuccess();
  };

  const handleCancelPendingPlan = async () => {
    if (!pendingPlanChange) return;
    setCancelingPlanChange(true);
    try {
      await cancelPlanChange(String(tenant.id), pendingPlanChange.id);
      setPendingPlanChange(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('No se pudo cancelar el cambio programado');
      showError(error.message);
    } finally {
      setCancelingPlanChange(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      showError(t('pages.tenants.validation.nameRequired'));
      return;
    }
    if (!formData.emailAdministrador.trim()) {
      showError(t('pages.tenants.validation.emailRequired'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAdministrador)) {
      showError(t('pages.tenants.validation.emailInvalid'));
      return;
    }
    if (!(formData.baseDeDatos ?? '').trim()) {
      showError(t('pages.tenants.validation.databaseRequired'));
      return;
    }
    if (
      savedForm &&
      formData.planAdminId !== savedForm.planAdminId &&
      savedForm.planAdminId != null &&
      forcePlan &&
      forcePlanReason.trim().length < 5
    ) {
      showError('Aplicar ya el plan: indicá un motivo (mínimo 5 caracteres)');
      return;
    }

    const invalid = commercialRef.current?.validate?.() ?? null;
    if (invalid) {
      showError(invalid);
      return;
    }

    const lines = [
      ...summarizeFormChanges(),
      ...(commercialRef.current?.summarize() ?? []),
    ];
    if (lines.length === 0) {
      showError('No hay cambios para guardar');
      return;
    }
    setConfirmLines(lines);
  };

  const handleConfirmSave = async () => {
    setIsLoading(true);
    try {
      await persistAll();
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Error al actualizar tenant');
      showError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const saasDirty =
    savedForm != null && formData.planAdminId !== savedForm.planAdminId;
  const commercialTabDirty = commercialDirty || saasDirty;

  const tabButtonClass = (tab: EditTenantTab) =>
    `inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
      activeTab === tab
        ? 'bg-teal-600 text-white shadow-sm dark:bg-teal-500 dark:text-slate-950'
        : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:hover:bg-gray-700 dark:hover:text-white'
    }`;

  const fieldClass =
    'block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white';

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      size="full"
      title={`${t('pages.tenants.edit')} - ${tenant.nombre}`}
      subtitle={t('pages.tenants.editModalSubtitle')}
      className="h-[92dvh] sm:h-[min(52rem,90dvh)]"
      bodyClassName="!flex !min-h-0 !flex-1 !flex-col !overflow-hidden !p-0"
      showCloseButton={false}
      closeOnOverlay={false}
      closeOnEscape={false}
      footer={
        confirmLines ? (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setConfirmLines(null)}
              disabled={isLoading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              isLoading={isLoading}
              onClick={() => void handleConfirmSave()}
            >
              {t('common.update')}
            </Button>
          </>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              isLoading={isLoading}
              disabled={isLoadingData}
            >
              {t('common.update')}
            </Button>
          </>
        )
      }
    >
      {isLoadingData ? (
        <div className="flex flex-1 items-center justify-center p-6">
          <div className="text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
        </div>
      ) : (
        <form id={formId} onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-b border-slate-200 bg-white/95 px-3 py-1.5 dark:border-gray-700 dark:bg-gray-800/95">
            <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 dark:border-gray-700 dark:bg-gray-900/50">
              <button
                type="button"
                className={tabButtonClass('data')}
                disabled={Boolean(confirmLines)}
                onClick={() => setActiveTab('data')}
              >
                {t('pages.tenants.tabs.data')}
              </button>
              <button
                type="button"
                className={tabButtonClass('commercial')}
                disabled={Boolean(confirmLines)}
                onClick={() => setActiveTab('commercial')}
              >
                {t('pages.tenants.tabs.commercial')}
                {commercialTabDirty ? (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                      activeTab === 'commercial'
                        ? 'bg-white/20 text-white'
                        : 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
                    }`}
                  >
                    Pendiente
                  </span>
                ) : null}
              </button>
            </div>
          </div>

          <div
            className={`min-h-0 flex-1 p-3 sm:p-4 ${
              activeTab === 'commercial' && !confirmLines
                ? 'flex flex-col overflow-hidden'
                : 'overflow-y-auto'
            }`}
          >
            {confirmLines ? (
              <div className="rounded-xl border border-teal-200 bg-teal-50/40 p-4 dark:border-teal-900 dark:bg-teal-950/20">
                <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                  Confirmá los cambios
                </h4>
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  Revisá lo configurado o modificado antes de guardar.
                </p>
                <ul className="mt-4 space-y-2">
                  {confirmLines.map((line) => (
                    <li
                      key={`${line.label}-${line.value}`}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40"
                    >
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        {line.label}
                      </p>
                      <p className="mt-0.5 text-sm text-gray-900 dark:text-white">{line.value}</p>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <div className={activeTab === 'data' && !confirmLines ? '' : 'hidden'}>
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
                <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                    {t('pages.tenants.sections.identity')}
                  </p>
                  <h4 className="mt-1 text-base font-semibold text-gray-900 dark:text-white">
                    {t('pages.tenants.sections.identityTitle')}
                  </h4>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <Input
                      label={t('pages.tenants.name')}
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      required
                    />

                    <Input
                      label={t('pages.tenants.email')}
                      type="email"
                      value={formData.emailAdministrador}
                      onChange={(e) =>
                        setFormData({ ...formData, emailAdministrador: e.target.value })
                      }
                      required
                    />

                    <Input
                      label={t('pages.tenants.phone')}
                      type="tel"
                      value={formData.telefonoContacto || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, telefonoContacto: e.target.value })
                      }
                      placeholder="+54 9 11 1234-5678"
                    />

                    <Input
                      label={t('pages.tenants.expirationDate')}
                      type="date"
                      value={
                        formData.fechaVencimiento
                          ? new Date(formData.fechaVencimiento).toISOString().split('T')[0]
                          : ''
                      }
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          fechaVencimiento: e.target.value
                            ? new Date(e.target.value)
                            : undefined,
                        })
                      }
                    />
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-gray-700 dark:bg-gray-900/20">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">
                    {t('pages.tenants.sections.technical')}
                  </p>
                  <h4 className="mt-1 text-base font-semibold text-gray-900 dark:text-white">
                    {t('pages.tenants.sections.technicalTitle')}
                  </h4>
                  <div className="mt-4 space-y-4">
                    <Input
                      label={t('pages.tenants.database')}
                      type="text"
                      value={formData.baseDeDatos ?? ''}
                      onChange={(e) =>
                        setFormData({ ...formData, baseDeDatos: e.target.value })
                      }
                      required
                    />

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t('pages.tenants.timeZone')}
                      </label>
                      <select
                        value={formData.timeZoneId}
                        onChange={(e) =>
                          setFormData({ ...formData, timeZoneId: e.target.value })
                        }
                        className={fieldClass}
                        required
                      >
                        {availableTimezones.map((tz) => (
                          <option key={tz} value={tz}>
                            {tz}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </section>
              </div>
            </div>

            <div
              className={
                activeTab === 'commercial' && !confirmLines
                  ? 'flex min-h-0 flex-1 flex-col gap-5'
                  : 'hidden'
              }
            >
              <section className="shrink-0">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <label
                          htmlFor="edit-tenant-saas-plan"
                          className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          {t('pages.tenants.saasPlanTitle')}
                        </label>
                        <span
                          className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-400/60 text-[10px] font-semibold text-slate-500 dark:border-slate-500 dark:text-slate-400"
                          title={t('pages.tenants.saasPlanHint')}
                          aria-hidden
                        >
                          i
                        </span>
                      </div>
                      <select
                        id="edit-tenant-saas-plan"
                        value={formData.planAdminId || ''}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            planAdminId: e.target.value
                              ? parseInt(e.target.value, 10)
                              : undefined,
                          })
                        }
                        className={`${fieldClass} mt-1.5 h-10`}
                      >
                        <option value="">{t('pages.tenants.selectPlan')}</option>
                        {plans
                          .filter(
                            (p) =>
                              p.id === formData.planAdminId ||
                              (p.isActive() && !isRetiredIntermedioPlan(p)),
                          )
                          .map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.nombre}
                              {!plan.isActive() ? ` (${t('pages.tenants.inactive')})` : ''}
                            </option>
                          ))}
                      </select>
                    </div>
                    <p className="pb-2.5 text-xs text-slate-500 dark:text-slate-400">
                      {t('pages.tenants.saasPlanSaveHint', { action: t('common.update') })}
                    </p>
                  </div>

                  {pendingPlanChange ? (
                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                      <span>
                        Cambio de plan programado: pasa a{' '}
                        <strong>{planName(pendingPlanChange.valorNuevoId ?? undefined)}</strong> el{' '}
                        {new Date(
                          `${pendingPlanChange.fechaEfectiva}T00:00:00Z`
                        ).toLocaleDateString('es-AR', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                          timeZone: 'UTC',
                        })}
                        .
                      </span>
                      <button
                        type="button"
                        onClick={handleCancelPendingPlan}
                        disabled={cancelingPlanChange}
                        className="shrink-0 font-semibold text-amber-900 underline hover:no-underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-amber-100"
                      >
                        {cancelingPlanChange ? 'Cancelando…' : 'Cancelar cambio programado'}
                      </button>
                    </div>
                  ) : saasDirty && savedForm?.planAdminId != null ? (
                    <div className="mt-2 space-y-2 rounded-lg border border-slate-200 p-3 dark:border-gray-700">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Por defecto el cambio de plan rige recién el 1° del próximo mes — este mes
                        se sigue facturando con el plan actual.
                      </p>
                      <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                        <input
                          type="checkbox"
                          checked={forcePlan}
                          onChange={(e) => setForcePlan(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                        />
                        Aplicar ahora (excepcional)
                      </label>
                      {forcePlan ? (
                        <Input
                          label="Motivo (auditoría)"
                          type="text"
                          value={forcePlanReason}
                          onChange={(e) => setForcePlanReason(e.target.value)}
                          placeholder="Ej. la clínica necesita el cambio urgente"
                        />
                      ) : null}
                    </div>
                  ) : null}
                </section>

                <div className="min-h-0 flex-1">
                  <TenantCommercialPacksSection
                    ref={commercialRef}
                    tenantId={tenant.id}
                    tenantName={tenant.nombre}
                    onDirtyChange={setCommercialDirty}
                  />
                </div>
            </div>
          </div>
        </form>
      )}
    </ModalShell>
  );
};
